import { useState } from 'react';
import type { Config } from '../engine/types.ts';
import { Column, Footer, Header, Progress } from '../shell/Shell.tsx';
import { OptionRow, OptionTile } from '../ui/OptionRow.tsx';
import { Confetti, FactTile, Ring, StatTile, Toast } from '../ui/bits.tsx';
import { PaymentMarks } from '../ui/PaymentMarks.tsx';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { allPlanValues } from '../engine/plans.ts';
import { CREAM, GRAD, INK, LINE, MUTED, ORANGE, PAPER, SHADOW_CARD, TIMER_BG } from '../ui/theme.ts';

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '20px 0', borderBottom: `2px solid ${LINE}` }}>
      <h2 style={{ margin: 0, fontSize: 12, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: MUTED }}>{title}</h2>
      {children}
    </section>
  );
}

/**
 * /kit — every shell state from PORT-EXACT on one page, for a visual diff
 * against design/funnel-kit-board.html. Dev route; not linked from the funnel.
 */
export function Kit({ config }: { config: Config }) {
  const [selected, setSelected] = useState(1);
  const plans = allPlanValues(config.settings);
  const sections = config.funnel.sections;

  return (
    <Column stepId="kit" stepType="kit">
      <Header canGoBack onBack={() => history.back()} brand={config.settings.brand.name} />
      <Progress label={sections[1]} segments={sections.map((s, i) => ({ section: s, width: i < 1 ? 100 : i === 1 ? 50 : 0 }))} />

      <main className="fk-main" style={{ flex: 1, overflow: 'auto', padding: '0 20px 24px' }}>
        <Block title="Title block">
          <TitleBlock label="We predict" title="More views on" highlight="every reel." subtitle="Subtitle in muted body." top={8} />
        </Block>

        <Block title="Progress · 0 / mid / 100">
          {[0, 50, 100].map((w) => (
            <div key={w} style={{ display: 'flex', gap: 6 }}>
              {sections.map((s, i) => (
                <div key={s} style={{ flex: 1, height: 8, borderRadius: 999, background: LINE, overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: GRAD, width: i === 0 ? `${w}%` : 0 }} />
                </div>
              ))}
            </div>
          ))}
        </Block>

        <Block title="Option rows">
          {['Get more views', 'Grow followers'].map((label, i) => (
            <OptionRow key={label} label={label} emoji={i ? '👥' : '🚀'} selected={selected === i} onSelect={() => setSelected(i)} />
          ))}
          <OptionRow label="Multi-select, checked" emoji="📤" checkbox role="checkbox" selected onSelect={() => undefined} />
          <OptionRow label="Multi-select, unchecked" emoji="🔁" checkbox role="checkbox" selected={false} onSelect={() => undefined} />
        </Block>

        <Block title="Two-up tiles">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <OptionTile label="Yes, absolutely" icon="check-circle" primary selected={selected === 0} onSelect={() => setSelected(0)} />
            <OptionTile label="Not sure yet" icon="question" primary={false} selected={selected === 1} onSelect={() => setSelected(1)} />
          </div>
        </Block>

        <Block title="Stat tiles · fact tile · ring">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <StatTile value="750M+" caption="views generated for creators" />
            <StatTile value="3.4×" caption="average reach after 30 days" highlight />
          </div>
          <FactTile text={config.content.shared.facts[0]} />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Ring />
          </div>
        </Block>

        <Block title="Timer card">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 20, background: TIMER_BG, color: '#fff' }}>
            <span style={{ width: 44, height: 44, borderRadius: 14, background: GRAD, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ph-duotone ph-timer" style={{ fontSize: 24 }} />
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', opacity: 0.8 }}>Your plan is reserved</div>
              <div style={{ fontSize: 13, fontWeight: 600, opacity: 0.9 }}>Price and plan held for you</div>
            </div>
            <div style={{ fontWeight: 800, fontSize: 22 }}>14:58</div>
          </div>
        </Block>

        <Block title="Plan cards">
          {plans.map((p, i) => (
            <div
              key={p.id}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                minHeight: 84,
                padding: '14px 16px',
                borderRadius: 20,
                background: i === 1 ? CREAM : PAPER,
                border: `2px solid ${i === 1 ? ORANGE : LINE}`,
              }}
            >
              <span style={{ flex: 1, fontWeight: 800 }}>{p.id}</span>
              <span style={{ textAlign: 'right' }}>
                {p.anchor ? <span style={{ display: 'block', fontSize: 12, color: MUTED, textDecoration: 'line-through' }}>{p.anchor}</span> : null}
                <span style={{ fontWeight: 800, fontSize: 26 }}>
                  {p.major}
                  <span style={{ fontSize: 14 }}>{p.cents}</span>
                </span>
                <span style={{ display: 'block', fontSize: 12, color: MUTED, fontWeight: 700 }}>
                  {p.hasIntro ? 'first week' : 'per week'} · save {p.savePct}%
                </span>
              </span>
            </div>
          ))}
        </Block>

        <Block title="Payment marks">
          <PaymentMarks />
        </Block>

        <Block title="Toast · confetti">
          <div style={{ position: 'relative', height: 90, background: CREAM, borderRadius: 20, boxShadow: SHADOW_CARD }}>
            <Confetti />
          </div>
          <div style={{ position: 'relative', height: 60 }}>
            <Toast message="→ app /upload" />
          </div>
        </Block>

        <Block title="CTA states">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, color: INK }}>
            <Footer cta={{ label: 'Start my plan', caption: 'Takes about 2 minutes · No Instagram login' }} />
            <Footer cta={{ label: 'Next', badge: '3' }} />
            <Footer cta={{ label: 'Next', disabled: true }} />
            <Footer cta={{ label: 'Generate my plan', icon: 'arrow-right', large: true }} />
            <Footer cta={{ label: 'Confirming your payment…', icon: 'circle-notch', spinner: true, disabled: true, large: true }} />
          </div>
        </Block>
      </main>
    </Column>
  );
}
